"use client";

/**
 * Componente boot — observa la sesión NextAuth y mantiene espejo en
 * localStorage para que las acciones offline sepan QUIÉN las realizó.
 *
 * Diseño:
 *   - Sólo escribe cuando la sesión está autenticada y cambia algo.
 *   - Limpia el espejo al perder sesión (logout).
 *   - No renderiza nada visible.
 */

import { useEffect } from "react";
import { useSession } from "next-auth/react";
import {
  clearSessionMirror,
  getSessionMirror,
  mirrorEquals,
  setSessionMirror,
} from "@/lib/session-mirror";
import { isOnline } from "@/lib/hooks/useNetworkStatus";
import { clearPermisosMirror } from "@/lib/offline/permisos-mirror";

export default function SessionMirrorBoot() {
  const { data: session, status } = useSession();

  useEffect(() => {
    if (status === "loading") return;

    if (status === "unauthenticated") {
      // CRÍTICO: cuando el dispositivo está offline, NextAuth puede reportar
      // "unauthenticated" por imposibilidad de contactar /api/auth/session.
      // En ese caso NO debemos limpiar el mirror — perderíamos la identidad
      // del usuario y romperíamos el sidebar y atribución de acciones offline.
      if (!isOnline()) return;
      // Sólo limpiamos si efectivamente había mirror — evita escrituras vacías.
      if (getSessionMirror()) clearSessionMirror();
      clearPermisosMirror();
      return;
    }

    const su = session?.user as
      | { id?: string; email?: string | null; name?: string | null; role?: string; image?: string | null }
      | undefined;
    if (!su?.id) return;

    const next = {
      userId: su.id,
      email: su.email ?? null,
      name: su.name ?? null,
      role: su.role ?? "user",
      image: su.image ?? null,
      personalId: null,
      telefono: null,
    };
    const current = getSessionMirror();
    if (!mirrorEquals(current, next)) {
      setSessionMirror(next);
    }
  }, [status, session]);

  return null;
}
